import { CircleX, Paperclip, FileIcon, ImageIcon, FileText, Table } from "lucide-react";
import PDFIcon from "@/assets/pdfIcon.svg";

import { toast } from "sonner";
import { cn } from "@/lib/utils";

// Get file icon based on extension
const getFileIcon = (fileName: string) => {
  const ext = (fileName.split(".").pop() || "").toLowerCase(); // FIXED

  if (ext === "pdf") {
    return <img src={PDFIcon} className="h-8 w-7 object-contain" alt="pdf-icon" />;
  }

  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
    return <ImageIcon className="text-g-500 h-6 w-6" />;
  }

  if (["doc", "docx"].includes(ext)) {
    return <FileText className="text-blue-500 h-6 w-6" />;
  }

  if (["xls", "xlsx"].includes(ext)) {
    return <Table className="text-green-600 h-6 w-6" />;
  }

  return <FileIcon className="text-g-500 h-6 w-6" />;
};


const FileInput = ({
  file,
  fileInputRef,
  setFile,
  accept,
  disabled,
  minSize, // in KB
  maxSize,
  uploadFileErrorMsg = "",
  onChange,
}: any) => {
  const handleClick = () => {
    if (!disabled) fileInputRef?.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (selectedFile) {
      const fileSizeInKB = selectedFile.size / 1024;

      if (
        (minSize && fileSizeInKB < minSize) ||
        (maxSize && fileSizeInKB > maxSize)
      ) {
        fileInputRef.current.value = null;

        toast.error(
          uploadFileErrorMsg ||
            `File size should be between ${minSize} KB and ${maxSize} KB`,
          {
            className: "!bg-red-500 !text-white",
          }
        );
        return;
      }

      setFile(selectedFile);
      onChange(selectedFile);
    }
  };

  return (
    <div className="flex flex-col w-full max-w-md text-start">
      {/* Display Box */}
      <div
        className={cn(
          "flex items-center justify-between w-full border border-gray-300 rounded-lg px-3 py-2 cursor-pointer hover:border-gray-400 h-[40px]",
          disabled ? "cursor-not-allowed" : ""
        )}
        onClick={handleClick}
      >
        <div className="flex flex-row gap-2 items-center">

          {/* Dynamic File Icon */}
          {file?.name && getFileIcon(file.name)}

          <div className="flex flex-col">
            <span
              className={`text-gray-400 text-sm ${
                file?.name ? "text-primary" : ""
              }`}
            >
              {file?.name || "Please attach"}
            </span>

            {file?.size && (
              <span className="text-[10px] text-g-300 -mt-1">
                size on file: {(file.size / 1024).toFixed(2)} KB
              </span>
            )}
          </div>
        </div>

        {file?.name ? (
          <CircleX
            className={cn(
              "text-gray-400 h-5 w-5",
              disabled ? "cursor-not-allowed" : ""
            )}
            onClick={(e) => {
              if (!disabled) {
                e.preventDefault();
                e.stopPropagation();
                setFile(null);
              }
            }}
          />
        ) : (
          <Paperclip className="text-gray-400 h-5 w-5" />
        )}
      </div>

      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept={accept}
        onChange={handleFileChange}
      />
    </div>
  );
};

export default FileInput;
